/**
 * Необязательное поле продавца на первом шаге нового входа и импорта (KAN-611).
 * KAN-670: продавец выбирается из справочника (селектор + кнопка добавления,
 * открывающая SellerDialog). KAN-673: отдельной ссылки на заказ нет, где брать
 * код показывает страница покупки из карточки продавца.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { SellerDialog } from '@/components/telegram/SellerDialog';
import { useTelegramSellers } from '@/hooks/useTelegramSellers';

export const NO_SELLER_LABEL = 'не указан';

export const SELLER_REQUIRED_TEXT = 'Для партии продавец обязателен';

interface Props {
  sellerId: string;
  disabled?: boolean;
  onChange: (sellerId: string) => void;
  /** KAN-776: в партии продавец обязателен: подпись без "(необязательно)" и подсказка, пока пусто. */
  required?: boolean;
}

export function ConnectSellerFields({
  sellerId,
  disabled,
  onChange,
  required = false,
}: Props): JSX.Element {
  const { data } = useTelegramSellers();
  const sellers = data ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const options = [
    { value: '', label: NO_SELLER_LABEL },
    ...sellers.map((s) => ({ value: s.id, label: s.name })),
  ];
  // выбранный продавец мог исчезнуть из справочника: селектор не должен "терять" значение
  const value = options.some((o) => o.value === sellerId) ? sellerId : '';

  return (
    <div className="space-y-1">
      <Label htmlFor="connect-seller-select">
        {required ? 'Продавец' : 'Продавец (необязательно)'}
      </Label>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <NativeSelect
            id="connect-seller-select"
            data-testid="connect-seller-select"
            aria-label="Продавец"
            options={options}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 flex-shrink-0"
          data-testid="connect-seller-add"
          aria-label="Добавить продавца"
          title="Добавить продавца"
          disabled={disabled}
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {required && !value && (
        <p className="text-xs text-amber-700" data-testid="batch-seller-required">
          {SELLER_REQUIRED_TEXT}
        </p>
      )}
      <SellerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={(seller) => onChange(seller.id)}
      />
    </div>
  );
}
